export const MONETIZATION_STATUS_EXECUTED = 2;
export const STATUS_PENDING = 'Pendiente';
export const STATUS_CANCELLED_EARLY = 'Cancelado anticipadamente';
export const STATUS_CANCELLED_EARLY_BADGE_LABEL = 'Cancelado antic.';
export const STATUS_OVERDUE = 'Vencida';

const normalizeStatusType = (statusType?: string | null): string =>
  (statusType || '').trim().toLowerCase();

export const formatInstallmentStatusLabel = (statusType?: string | null): string => {
  if (normalizeStatusType(statusType) === STATUS_CANCELLED_EARLY.toLowerCase()) {
    return STATUS_CANCELLED_EARLY_BADGE_LABEL;
  }
  return (statusType || '').trim() || '-';
};

/** Icono visible en plan de cuotas (Figma): pendiente o ya cancelada anticipadamente. */
export const shouldShowInstallmentEarlyCancellationIcon = (params: {
  earlyCancellationEnabled: boolean;
  statusType?: string | null;
}): boolean => {
  if (!params.earlyCancellationEnabled) return false;
  const status = normalizeStatusType(params.statusType);
  return (
    status === STATUS_PENDING.toLowerCase() ||
    status === STATUS_CANCELLED_EARLY.toLowerCase()
  );
};

export const resolveEarlyCancellationInstrumentType = (
  guaranteeType?: string | null,
): string => {
  const normalized = guaranteeType?.trim().toLowerCase().replace(/-/g, '_');
  if (normalized === 'loan') return 'loan';
  if (normalized === 'check') return 'check';
  if (normalized === 'promissory_note') return 'promissory-note';
  if (normalized === 'commercial_endorsement') return 'commercial-endorsement';
  return 'check';
};

export const isInstrumentEarlyCancellationEligible = (params: {
  monetizationStatusTypeId?: number | null;
  statusType?: string | null;
  originalDueDate?: string | null;
}): boolean => {
  const { monetizationStatusTypeId, statusType, originalDueDate } = params;
  if (originalDueDate) return false;
  if (monetizationStatusTypeId !== MONETIZATION_STATUS_EXECUTED) return false;
  return (statusType || '').trim().toLowerCase() === STATUS_PENDING.toLowerCase();
};

export const isInstallmentEarlyCancellationEligible = (params: {
  statusType?: string | null;
  originalDueDate?: string | null;
}): boolean => {
  if (params.originalDueDate) return false;
  return (params.statusType || '').trim().toLowerCase() === STATUS_PENDING.toLowerCase();
};

/** Compare calendar days: selected must be <= dueDate. Returns null if valid. */
export const validateEarlyCancellationDate = (
  selectedDate: string | null | undefined,
  dueDate: string | null | undefined,
): string | null => {
  if (!selectedDate) return 'La fecha es obligatoria';
  if (!dueDate) return 'No hay fecha de vencimiento vigente';
  const selected = selectedDate.split('T')[0];
  const due = dueDate.split('T')[0];
  if (selected > due) {
    return 'La fecha no puede ser posterior al vencimiento vigente';
  }
  return null;
};

export interface LoanEarlyCancellationPreview {
  reached: number;
  skippedPending: number;
  overdue: number;
}

export const previewLoanEarlyCancellation = (
  installments: Array<{ due_date?: string; status_type?: string }>,
  selectedDate: string,
): LoanEarlyCancellationPreview => {
  const selected = selectedDate.split('T')[0];
  let reached = 0;
  let skippedPending = 0;
  let overdue = 0;
  installments.forEach((inst) => {
    const status = (inst.status_type || '').trim();
    const due = (inst.due_date || '').split('T')[0];
    if (status.toLowerCase() === STATUS_OVERDUE.toLowerCase()) {
      overdue += 1;
      return;
    }
    if (status.toLowerCase() !== STATUS_PENDING.toLowerCase() || !due) return;
    if (due < selected) skippedPending += 1;
    else reached += 1;
  });
  return { reached, skippedPending, overdue };
};

export const formatOriginalDueDateDisplay = (value?: string | null): string => {
  if (!value) return '-';
  const [year, month, day] = value.split('T')[0].split('-');
  if (!year || !month || !day) return '-';
  return `${day}/${month}/${year}`;
};

/** Count installments in Pendiente (eligible for early-cancellation pool). */
export const countPendingInstallments = (
  installments: Array<{ status_type?: string; statusType?: string }>,
): number =>
  installments.filter((inst) => {
    const status = (inst.status_type ?? inst.statusType ?? '').trim();
    return status.toLowerCase() === STATUS_PENDING.toLowerCase();
  }).length;

export const resolveOperationTypeLabel = (data: any): string =>
  data?.operation_type ||
  data?.guarantee_type_name ||
  data?.guarantee_type ||
  data?.guaranteeTypeName ||
  '-';
